import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo,useRef, useState } from 'react';

import { ActionItemStatus } from '@ekhodealer/ekho-common';
import {
	Button,
	Divider,
	Group,
	SkeletonRect,
	Stack,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { ActionItem, FormScan } from '@ekhodealer/ekho-common';

import Upload16 from '@common/components/icons/Upload16';
import { formatTimestampToDate } from '@common/lib/time';

import { useGetScanAndUploadForms } from '../../../api/progress/useGetScanAndUploadForms';
import { useScanAndUploadForms } from '../../../api/progress/useScanAndUploadForms';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

import { fileToBase64 } from './utils/fileToBase64';

interface ScanAndUploadFormsDetailProps {
	actionItem: ActionItem;
}

/**
 * Represents a form that needs to be scanned and uploaded
 */
interface FormUploadState {
	formKey: string;
	formName: string;
	file: File | null;
	isUploaded: boolean;
	isAlreadyUploaded: boolean;
}

/**
 * ScanAndUploadFormsDetail component for uploading MSO scans.
 * Follows the same upload pattern as ShipSellerFormsToEkhoDetail.
 * TODO: Once we support used vehicles, update to support Title uploads as well.
 */
export const ScanAndUploadFormsDetail = ({ actionItem }: ScanAndUploadFormsDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	// Create ref for file input
	const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

	const scanFormsMutation = useScanAndUploadForms(orderId);
	const { data: formData, isLoading: isLoadingFormData } = useGetScanAndUploadForms(orderId);

	// Track uploaded files state
	const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});

	// Build form states from form data
	const formStates: FormUploadState[] = useMemo(() => {
		if (!formData?.documentsToUpload) return [];

		return formData.documentsToUpload.map((formName) => {
			// Use form name as key
			const formKey = formName;
			const isAlreadyUploaded = formData.uploadedScans?.[formKey] === true;
			const hasLocalFile = !!uploadedFiles[formKey];

			return {
				formKey,
				formName,
				file: uploadedFiles[formKey] || null,
				isUploaded: isAlreadyUploaded || hasLocalFile,
				isAlreadyUploaded,
			};
		});
	}, [formData, uploadedFiles]);

	// Check if all forms have been uploaded
	const allFormsUploaded = useMemo(() => {
		if (formStates.length === 0) return false;
		return formStates.every((form) => form.isUploaded);
	}, [formStates]);

	const handleFileSelect = (formKey: string, file: File) => {
		setUploadedFiles((prev) => ({
			...prev,
			[formKey]: file,
		}));
	};

	const handleReplaceFile = (formKey: string) => {
		// Remove the file from local state
		setUploadedFiles((prev) => {
			const newFiles = { ...prev };
			delete newFiles[formKey];
			return newFiles;
		});
	};

	const handleSubmit = async () => {
		try {
			// Convert all uploaded files to FormScan format
			const formScans: FormScan[] = await Promise.all(
				Object.entries(uploadedFiles).map(async ([formKey, file]) => {
					const base64Data = await fileToBase64(file);
					return {
						formKey,
						file: {
							base64Data,
							type: file.type || 'application/pdf',
							name: file.name,
						},
					};
				}),
			);

			await scanFormsMutation.mutateAsync({ formScans });

			toast({
				message: 'Form scans uploaded successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to upload form scans';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	};

	const isFormDisabled =
		scanFormsMutation.isPending || actionItem.status === ActionItemStatus.COMPLETE;

	// If already complete, show completed state
	if (actionItem.status === ActionItemStatus.COMPLETE) {
		return (
			<Stack itemsSpacing='24'>
				<Stack itemsSpacing='8'>
					<Text size='bodySmall' color='success'>
						✓ Forms have been scanned and uploaded
					</Text>
				</Stack>
				{formData?.formsScanCompleteDate && (
					<Text size='bodySmall' color='secondary'>
						Uploaded on {formatTimestampToDate(new Date(formData.formsScanCompleteDate).getTime()) ?? '—'}
					</Text>
				)}
			</Stack>
		);
	}

	return (
		<Stack itemsSpacing='24'>
			{/* Header */}
			<Stack itemsSpacing='8'>
				<Text size='bodySmall' color='secondary'>
					{'Please upload scans of the documents listed below for record keeping.'}
				</Text>
			</Stack>

			{isLoadingFormData ? (
				<Stack itemsSpacing='8'>
					<SkeletonRect width='60%' height={20} />
					<SkeletonRect width='80%' height={20} />
				</Stack>
			) : (
				<>
					{/* Upload section */}
					<Stack itemsSpacing='16'>
						<Text size='body' weight='semibold'>
							Upload scans of the following forms
						</Text>

						<Stack itemsSpacing='16'>
							{formStates.map((form) => (
								<Stack key={form.formKey} itemsSpacing='16'>
									<input
										ref={(el) => {
											fileInputRefs.current[form.formKey] = el;
										}}
										type='file'
										accept='.pdf,.png,.jpg,.jpeg'
										onChange={(e) => {
											const file = e.target.files?.[0];
											if (file) {
												handleFileSelect(form.formKey, file);
											}
										}}
										style={{ display: 'none' }}
										disabled={isFormDisabled}
									/>
									<Group
										itemsSpacing='8'
										itemsAlignY='center'
										itemsAlignX='space-between'>
										<Stack itemsSpacing='2'>
											<Text size='bodySmall'>{form.formName}</Text>
											{form.file && (
												<Text size='footnote' color='secondary'>
													{form.file.name}
												</Text>
											)}
										</Stack>

										{form.isUploaded ? (
											form.isAlreadyUploaded ? (
												<Text size='bodySmall' color='success'>
													✓ Uploaded
												</Text>
											) : (
												<Group itemsSpacing='8' itemsAlignY='center'>
													<Text size='bodySmall' color='secondary'>
														Uploaded
													</Text>
													<Button
														variant='ghost'
														size='small'
														onPress={() => {
															handleReplaceFile(form.formKey);
														}}
														isDisabled={isFormDisabled}
														aria-label={`Replace ${form.formName}`}>
														Replace
													</Button>
												</Group>
											)
										) : (
											<Button
												variant='secondary'
												size='medium'
												onPress={() => {
													fileInputRefs.current[form.formKey]?.click();
												}}
												isDisabled={isFormDisabled}
												aria-label={`Upload ${form.formName}`}>
												<Group itemsSpacing='4' itemsAlignY='center'>
													<Upload16 />
													<Text size='bodySmall'>Upload</Text>
												</Group>
											</Button>
										)}
									</Group>
									<Divider />
								</Stack>
							))}
						</Stack>
					</Stack>

					{/* Action buttons */}
					<Group itemsSpacing='8' noWrap>
						<Group.Item grow={1}>
							<Button
								variant='secondary'
								size='large'
								isFullWidth
								onPress={navigateBack}
								isDisabled={scanFormsMutation.isPending}>
								Cancel
							</Button>
						</Group.Item>
						<Group.Item grow={1}>
							<Button
								size='large'
								isFullWidth
								onPress={handleSubmit}
								isLoading={scanFormsMutation.isPending}
								isDisabled={
									!allFormsUploaded || isLoadingFormData || isFormDisabled
								}>
								{allFormsUploaded
									? actionItem.config.buttonTitle || 'Upload form scans'
									: `Upload ${formStates.length} form(s) to continue`}
							</Button>
						</Group.Item>
					</Group>
				</>
			)}
		</Stack>
	);
};
